import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useConsumableMutations } from '../hooks/useConsumables';
import type { ConsumableType, ConsumableTypeFormData } from '../types';

const UNIT_OPTIONS = [
  { value: 'units', label: 'Units' },
  { value: 'liters', label: 'Liters' },
  { value: 'ml', label: 'Milliliters (ml)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'g', label: 'Grams (g)' },
  { value: 'rolls', label: 'Rolls' },
  { value: 'boxes', label: 'Boxes' },
  { value: 'sheets', label: 'Sheets' },
  { value: 'meters', label: 'Meters' },
  { value: 'bottles', label: 'Bottles' },
  { value: 'cartridges', label: 'Cartridges' },
  { value: 'drums', label: 'Drums' },
  { value: 'pouches', label: 'Pouches' },
];

interface ConsumableTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consumableType?: ConsumableType | null;
}

const emptyForm: ConsumableTypeFormData = {
  name: '',
  unit: 'units',
  category: '',
  unit_cost: '',
  low_stock_threshold: '',
};

export default function ConsumableTypeModal({
  open,
  onOpenChange,
  consumableType,
}: ConsumableTypeModalProps) {
  const isEditing = Boolean(consumableType);
  const { createType, updateType } = useConsumableMutations();

  const [formData, setFormData] = useState<ConsumableTypeFormData>(emptyForm);

  useEffect(() => {
    if (consumableType) {
      setFormData({
        name: consumableType.name,
        unit: consumableType.unit,
        category: consumableType.category ?? '',
        unit_cost: consumableType.unit_cost != null ? String(consumableType.unit_cost) : '',
        low_stock_threshold:
          consumableType.low_stock_threshold != null
            ? String(consumableType.low_stock_threshold)
            : '',
      });
    } else {
      setFormData(emptyForm);
    }
  }, [consumableType, open]);

  const resetForm = () => setFormData(emptyForm);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const payload = {
      name: formData.name.trim(),
      unit: formData.unit,
      category: formData.category.trim() || undefined,
      unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : undefined,
      low_stock_threshold: formData.low_stock_threshold
        ? parseFloat(formData.low_stock_threshold)
        : undefined,
    };

    if (isEditing && consumableType) {
      updateType.mutate(
        { id: consumableType.id, ...payload },
        {
          onSuccess: () => {
            onOpenChange(false);
            resetForm();
          },
        }
      );
    } else {
      createType.mutate(payload, {
        onSuccess: () => {
          onOpenChange(false);
          resetForm();
        },
      });
    }
  };

  const isLoading = createType.isPending || updateType.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Consumable Type' : 'Add Consumable Type'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details for this consumable type.'
              : 'Define a new consumable to track inventory and costs.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ct-name">Name *</Label>
              <Input
                id="ct-name"
                placeholder="e.g., DTG White Ink"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-unit">Unit *</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) =>
                    setFormData({ ...formData, unit: value })
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger id="ct-unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ct-cost">Unit Cost ($)</Label>
                <Input
                  id="ct-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.unit_cost}
                  onChange={(e) =>
                    setFormData({ ...formData, unit_cost: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-category">Category</Label>
                <Input
                  id="ct-category"
                  placeholder="e.g., Ink, Packaging"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ct-threshold">Low Stock Alert</Label>
                <Input
                  id="ct-threshold"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Optional"
                  value={formData.low_stock_threshold}
                  onChange={(e) =>
                    setFormData({ ...formData, low_stock_threshold: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !formData.name.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : isEditing ? (
                'Update Type'
              ) : (
                'Create Type'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
